import { Outlet } from "react-router-dom";
import { useGlobalStore } from "@/GlobalStore";
import { AccountStatus, Account } from "@/types";
import { SqButton } from "@/components/squircle-ui/button";
import { RiKey2Fill } from "@remixicon/react";
import { startGoogleAdsAuth } from "@/api/globalApiService";

interface ExpiredTokenGuardProps {
  children?: React.ReactNode;
  title?: string;
  description?: React.ReactNode | ((account: Account | null) => React.ReactNode);
}

const ExpiredTokenGuard = ({ children, title, description }: ExpiredTokenGuardProps) => {
  const selectedAccount = useGlobalStore((state) => state.selectedAccount);
  const isViewer = selectedAccount?.role === "viewer";
  if (selectedAccount?.status !== AccountStatus.EXPIRED)
    return <>{children ?? <Outlet />}</>;

  const handleReconnect = () => {
    if (!selectedAccount?.platform) return;
    void (async () => {
      try {
        const platform = selectedAccount.platform;
        const authUrl = await startGoogleAdsAuth(window.location.href, platform, selectedAccount.ad_account_id);
        window.open(authUrl, "_self");
      } catch {
        // ignore
      }
    })();
  };

  return (
    <div className="flex flex-col items-center justify-center h-full w-[30%] m-auto">
      <div className="flex items-center justify-center">
        <svg
          width="240"
          height="178"
          viewBox="0 0 240 178"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M69.3949 28.8102C69.3949 28.8102 96.4163 49.3158 137.496 10.9741C173.978 -23.0763 203.767 30.6476 203.976 56.375C204.246 89.702 167.493 116.369 185.329 138.258C203.165 160.148 149.957 196.295 121.281 165.012C85.6093 126.097 75.9454 157.716 55.6125 157.716C41.0193 157.716 11.0582 121.456 31.2906 94.4791C48.316 71.7787 39.0307 64.2449 34.5341 56.375C28.0478 45.0248 43.4516 14.2172 69.3949 28.8102Z"
            fill="#ECEDEF"
          />
          <path
            d="M177.877 74.1245C179.329 74.6642 180.917 74.7001 182.427 74.3535C188.754 72.9012 210.509 69.0559 206.729 85.9145C205.906 89.2593 193.164 107.991 211.31 114.829C212.639 115.336 214.045 115.613 215.468 115.648C221.055 115.763 232.126 114.444 236.76 109.727C242.357 104.029 236.242 106.731 236.242 106.731C236.242 106.731 214.962 116.018 207.446 106.166C206.609 105.069 206.077 103.477 205.924 102.107C205.66 99.7302 206.715 96.5899 208.101 93.8563C209.977 90.1564 218.984 73.5129 199.429 68.6644C194.924 67.5917 188.381 66.628 179.945 70.18C175.024 72.2517 176.114 73.4694 177.877 74.1245Z"
            fill="#1F3883"
          />
          <path
            d="M182.231 67.9107L176.568 70.0305C174.612 70.7623 173.621 72.9405 174.352 74.8956L174.633 75.6458C175.365 77.601 177.543 78.5928 179.498 77.861L185.162 75.7412C187.117 75.0095 188.109 72.8313 187.377 70.8761L187.097 70.1259C186.365 68.1708 184.187 67.179 182.231 67.9107Z"
            fill="#021859"
          />
          <path
            d="M128.89 81.3176L147.57 74.3265C148.392 74.0189 149.308 74.4361 149.616 75.2582L149.69 75.4548C149.998 76.2772 149.58 77.1932 148.758 77.5011L130.078 84.4922C129.256 84.7998 128.34 84.3828 128.032 83.5605L127.958 83.3639C127.651 82.5417 128.068 81.6255 128.89 81.3176Z"
            fill="white"
            stroke="#1F3883"
            stroke-width="0.6"
          />
          <path
            d="M134.103 95.2464L152.783 88.2552C153.606 87.9475 154.522 88.3647 154.83 89.1869L154.903 89.3835C155.211 90.2059 154.794 91.1219 153.971 91.4298L135.291 98.421C134.469 98.7285 133.553 98.3116 133.245 97.4892L133.172 97.2926C132.864 96.4704 133.281 95.5542 134.103 95.2464Z"
            fill="white"
            stroke="#1F3883"
            stroke-width="0.6"
          />
          <path
            d="M169.924 64.5393L154.784 70.2053C152.829 70.9371 151.837 73.1153 152.569 75.0705L157.738 88.881C158.47 90.8362 160.648 91.828 162.603 91.0963L177.742 85.4302C179.698 84.6985 180.689 82.5203 179.958 80.5651L174.789 66.7545C174.057 64.7993 171.879 63.8075 169.924 64.5393Z"
            fill="#FF4500"
          />
          <path
            d="M156.348 68.1623L141.209 73.8284C139.254 74.5601 138.262 76.7383 138.994 78.6935L145.16 95.1701C145.892 97.1253 148.07 98.1171 150.026 97.3853L165.165 91.7193C167.12 90.9875 168.112 88.8093 167.38 86.8541L161.214 70.3775C160.482 68.4223 158.304 67.4306 156.348 68.1623Z"
            fill="#FF4500"
          />
          <path
            d="M158.718 64.8193L158.599 64.8638C157.296 65.3516 156.635 66.8037 157.123 68.1072L165.761 91.1891C166.249 92.4926 167.701 93.1538 169.005 92.666L169.123 92.6215C170.427 92.1337 171.088 90.6816 170.6 89.3781L161.962 66.2962C161.474 64.9927 160.022 64.3315 158.718 64.8193Z"
            fill="#021859"
          />
          <path
            d="M4.3477 79.5765C4.3477 79.5765 34.7525 71.032 30.3382 90.7178C29.5716 93.8327 18.5367 109.871 31.681 117.572C34.5034 119.225 37.7605 119.958 41.0313 119.94C46.8958 119.91 56.1927 118.781 60.3692 114.53C65.9666 108.832 59.8516 111.534 59.8516 111.534C59.8516 111.534 42.3896 118.717 33.4061 112.991C30.3386 111.036 28.9482 107.251 29.7812 103.71C30.2069 101.951 30.8553 100.254 31.7105 98.659C33.6642 94.9997 42.5934 78.3157 23.0382 73.4672C18.5334 72.3944 11.9906 71.4308 3.55428 74.9827C-4.88205 78.5346 4.3477 79.5765 4.3477 79.5765Z"
            fill="#1F3883"
          />
          <path
            d="M64.457 107.128L58.7931 109.248C56.8379 109.98 55.8461 112.158 56.5779 114.113L56.8586 114.864C57.5904 116.819 59.7686 117.811 61.7238 117.079L67.3877 114.959C69.3429 114.227 70.3347 112.049 69.6029 110.094L69.3222 109.344C68.5904 107.389 66.4122 106.397 64.457 107.128Z"
            fill="#021859"
          />
          <path
            d="M74.0267 97.8097L65.6325 100.951C63.6773 101.683 62.6855 103.861 63.4172 105.817L67.4649 116.632C68.1966 118.587 70.3748 119.579 72.33 118.847L80.7243 115.705C82.6794 114.973 83.6712 112.795 82.9395 110.84L78.8918 100.025C78.1601 98.0698 75.9819 97.078 74.0267 97.8097Z"
            fill="#FF4500"
          />
          <path
            d="M88.0012 89.4831L75.483 94.1682C73.5279 94.9 72.5361 97.0782 73.2678 99.0334L79.4344 115.51C80.1661 117.465 82.3443 118.457 84.2995 117.725L96.8177 113.04C98.7729 112.308 99.7647 110.13 99.0329 108.175L92.8664 91.6983C92.1346 89.7431 89.9564 88.7513 88.0012 89.4831Z"
            fill="#FF4500"
          />
          <path
            d="M90.6527 86.0713L90.5339 86.1157C89.2305 86.6036 88.5693 88.0557 89.0571 89.3591L97.6959 112.441C98.1837 113.745 99.6358 114.406 100.939 113.918L101.058 113.873C102.361 113.386 103.023 111.933 102.535 110.63L93.8961 87.5481C93.4083 86.2446 91.9561 85.5835 90.6527 86.0713Z"
            fill="#021859"
          />
          <path
            d="M122.619 71.3665C122.168 71.3165 121.754 71.0935 121.464 70.7445C121.175 70.3955 121.032 69.9477 121.065 69.4954L121.999 52.435C122.055 51.4159 122.916 50.6417 123.892 50.7317L125.253 50.8577C126.23 50.9478 126.929 51.8657 126.792 52.8776L124.497 69.8121C124.444 70.2632 124.22 70.6765 123.87 70.9661C123.52 71.2556 123.072 71.3991 122.619 71.3665Z"
            fill="white"
          />
          <path
            d="M114.554 72.8371C114.413 72.8783 114.266 72.8915 114.121 72.8759C113.976 72.8603 113.835 72.8162 113.707 72.7462C113.578 72.6761 113.465 72.5815 113.373 72.4677C113.282 72.3539 113.213 72.2232 113.172 72.083L109.837 61.4705C109.74 61.1687 109.766 60.8407 109.911 60.5586C110.055 60.2764 110.306 60.0631 110.608 59.9654L111.471 59.7049C111.619 59.6616 111.775 59.6496 111.928 59.6695C112.081 59.6894 112.228 59.7408 112.36 59.8205C112.493 59.9001 112.607 60.0064 112.696 60.1324C112.785 60.2585 112.847 60.4016 112.878 60.5529L115.35 71.426C115.419 71.7183 115.375 72.0258 115.228 72.2873C115.08 72.5488 114.839 72.7451 114.554 72.8371Z"
            fill="white"
          />
          <path
            d="M110.053 78.9571C109.455 79.6976 108.253 79.6888 107.328 78.9371L90.9014 65.591C89.9202 64.7938 89.6307 63.4742 90.2647 62.6892L91.1484 61.5945C91.7824 60.8092 93.0846 60.8735 94.0131 61.7361L109.556 76.1771C110.431 76.9903 110.65 78.2166 110.053 78.9571Z"
            fill="white"
          />
          <path
            d="M117.646 113.429C118.069 113.592 118.412 113.913 118.603 114.325C118.794 114.736 118.818 115.206 118.67 115.634L113.41 131.89C113.096 132.861 112.066 133.389 111.145 133.053L109.861 132.584C108.939 132.248 108.497 131.181 108.888 130.238L115.432 114.452C115.598 114.029 115.921 113.686 116.334 113.496C116.746 113.305 117.216 113.281 117.646 113.429Z"
            fill="white"
          />
          <path
            d="M125.819 114.066C125.965 114.062 126.11 114.087 126.247 114.139C126.383 114.191 126.508 114.27 126.615 114.37C126.721 114.47 126.806 114.591 126.866 114.724C126.925 114.858 126.958 115.002 126.962 115.148L127.476 126.26C127.493 126.577 127.384 126.887 127.172 127.123C126.96 127.359 126.663 127.501 126.347 127.519L125.445 127.55C125.291 127.554 125.137 127.526 124.995 127.467C124.852 127.409 124.722 127.322 124.615 127.211C124.507 127.1 124.424 126.968 124.37 126.824C124.316 126.679 124.293 126.525 124.302 126.371L124.689 115.227C124.697 114.927 124.818 114.641 125.027 114.425C125.236 114.21 125.519 114.082 125.819 114.066Z"
            fill="white"
          />
          <path
            d="M131.734 109.299C132.501 108.735 133.661 109.051 134.363 110.014L146.837 127.112C147.582 128.133 147.525 129.483 146.711 130.08L145.577 130.913C144.763 131.51 143.521 131.115 142.843 130.044L131.503 112.113C130.866 111.103 130.967 109.862 131.734 109.299Z"
            fill="white"
          />
        </svg>
      </div>
      <div className="mt-[48px] text-center font-medium text-[16px] leading-[100%] tracking-[0%] text-brand-primary-new">
        {title ?? "Account access is expired"}
      </div>
      {description && !isViewer ? (
        <p className="text-center font-normal text-[14px] leading-[120%] tracking-[0%] text-brand-primary-600 mt-[12px]">
          {typeof description === "function" ? description(selectedAccount) : description}
        </p>
      ) : isViewer ? (
        <p className="text-center font-normal text-[14px] leading-[120%] tracking-[0%] text-brand-primary-600 mt-[12px]">
          The <span className="lowercase">{selectedAccount?.platform}</span> connection
          has expired. You don't have permission to reconnect, please ask an admin or
          editor to re-link it.
        </p>
      ) : (
        <p className="text-center font-normal text-[14px] leading-[120%] tracking-[0%] text-brand-primary-600 mt-[12px]">
          We can't pull new data from{" "}
          <span className="text-brand-primary-800 font-medium">
            {selectedAccount?.name}
          </span>{" "}
          right now. Reconnect{" "}
          <span className="lowercase">{selectedAccount?.platform}</span> to restore
          automatic syncing.
        </p>
      )}
      {!isViewer && (
        <SqButton
          className="mt-[34px]"
          variant={"outline"}
          size={"md"}
          onClick={handleReconnect}
        >
          <RiKey2Fill size={16} className="text-brand-primary-400 shrink-0" />
          <span className="text-brand-primary-800 font-medium">
            Log in with{" "}
            <span>
              {selectedAccount?.platform
                ? selectedAccount.platform.charAt(0).toUpperCase() +
                  selectedAccount.platform.slice(1).toLowerCase()
                : ""}
            </span>
          </span>
        </SqButton>
      )}
    </div>
  );
};

export default ExpiredTokenGuard;
